import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Sparkles, Lock, Eye, EyeOff, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

function apiErr(e) {
  const d = e?.response?.data?.detail;
  if (!d) return e?.message || "Something went wrong";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg || JSON.stringify(x)).join(" • ");
  return typeof d === "object" ? d.msg || JSON.stringify(d) : String(d);
}

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) toast.error("Missing reset token");
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 8) return toast.error("Password must be at least 8 characters");
    if (pw !== confirm) return toast.error("Passwords do not match");
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      setDone(true);
      toast.success("Password updated ✨");
    } catch (err) {
      toast.error(apiErr(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Navbar />
      <div className="max-w-md mx-auto px-6 pt-32 pb-16">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full glass mb-6">
          <Sparkles className="w-3 h-3 text-secondary" />
          <span className="text-[10px] uppercase tracking-widest font-semibold">Reset password</span>
        </div>
        <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter">
          Set a new password
        </h1>
        <p className="text-muted-foreground mt-3">
          Choose something memorable, but strong — at least 8 characters.
        </p>

        {done ? (
          <div className="mt-10 glass rounded-3xl p-8 text-center space-y-4">
            <div className="w-16 h-16 rounded-full bg-emerald-500/10 mx-auto flex items-center justify-center">
              <CheckCircle2 className="w-8 h-8 text-emerald-500" />
            </div>
            <div className="font-display text-2xl font-bold">All set.</div>
            <p className="text-sm text-muted-foreground">
              Your password has been updated. You can sign in with your new credentials now.
            </p>
            <Button
              onClick={() => navigate("/")}
              data-testid="reset-done-continue"
              className="w-full h-12 rounded-2xl bg-primary hover:bg-primary/90 text-primary-foreground"
            >
              Back to Luminon
            </Button>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-10 glass rounded-3xl p-8 space-y-5">
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">New password</Label>
              <div className="relative mt-1.5">
                <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  data-testid="reset-password-input"
                  type={showPw ? "text" : "password"}
                  value={pw}
                  onChange={(e) => setPw(e.target.value)}
                  placeholder="At least 8 characters"
                  required
                  className="h-12 pl-11 pr-11 rounded-2xl"
                />
                <button
                  type="button"
                  onClick={() => setShowPw((s) => !s)}
                  className="absolute right-3.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  aria-label="Toggle password visibility"
                >
                  {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Confirm password</Label>
              <div className="relative mt-1.5">
                <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  data-testid="reset-confirm-input"
                  type={showPw ? "text" : "password"}
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  placeholder="Repeat password"
                  required
                  className="h-12 pl-11 rounded-2xl"
                />
              </div>
            </div>
            <Button
              type="submit"
              disabled={busy || !token}
              data-testid="reset-submit-btn"
              className="w-full h-12 rounded-2xl bg-primary hover:bg-primary/90 text-primary-foreground font-medium"
            >
              {busy ? "Updating…" : "Update password"}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
